import { useEffect, useRef } from "react";

import type { createTimelineStore } from "../store/timeline";
import type { createTimelineDataStore } from "../store/timeline-data";
import { focusTargetId, isTimelineFocusTarget } from "../utils/focus";
import { findNodeById, trackToBars } from "../utils/tree";

/** Focus belongs to a media source and addressable nodes, independent of tracking work. */
export function useTimelineFocusLifecycle(
  source: string,
  timelineStore: ReturnType<typeof createTimelineStore>,
  dataStore: ReturnType<typeof createTimelineDataStore>,
) {
  const previousSource = useRef(source);
  useEffect(() => {
    if (previousSource.current !== source) {
      previousSource.current = source;
      timelineStore.getState().selectSegment(null);
    }
    const reconcile = () => {
      const { items } = dataStore.getState();
      const state = timelineStore.getState();
      if (state.hoveredTimelineRow && !isTimelineFocusTarget(findNodeById(items, state.hoveredTimelineRow.id))) {
        state.clearHoveredTimelineRow(state.hoveredTimelineRow.id, state.hoveredTimelineRow.area);
      }
      if (
        state.selectedSegmentId &&
        !isTimelineFocusTarget(findNodeById(items, focusTargetId(state.selectedSegmentId)))
      ) {
        state.selectSegment(null);
      }
      const hoveredId = timelineStore.getState().hoveredTimelineSegmentId;
      if (hoveredId) {
        const node = findNodeById(items, focusTargetId(hoveredId));
        if (
          !isTimelineFocusTarget(node) ||
          (node?.type === "track" && !trackToBars(node).some((bar) => bar.id === hoveredId))
        ) {
          // A summary bar's child can disappear while its collection survives.
          state.setHighlightedSegmentIds([]);
        }
      }
      const ids = timelineStore.getState().highlightedSegmentIds;
      const retained = ids.filter((id) => isTimelineFocusTarget(findNodeById(items, focusTargetId(id))));
      if (retained.length !== ids.length) state.setHighlightedSegmentIds(retained);
    };
    reconcile();
    return dataStore.subscribe((state, previous) => {
      if (state.items !== previous.items) reconcile();
    });
  }, [source, timelineStore, dataStore]);
}
